import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Target, Lock, ArrowUp, ArrowDown, Eye, Scroll, Flame, Trophy, History } from 'lucide-react';
import { CINEMATIC_KEYFRAMES, ScanlineOverlay, NoiseOverlay, GasParticles, ToxicDrips, SewerFrame } from './CinematicStyles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) => {
  if (!price || price === 0) return '$0.00';
  if (price < 0.00000001) return `$${price.toFixed(12)}`;
  if (price < 0.0000001) return `$${price.toFixed(11)}`;
  if (price < 0.000001) return `$${price.toFixed(10)}`;
  if (price < 0.00001) return `$${price.toFixed(9)}`;
  if (price < 0.0001) return `$${price.toFixed(8)}`;
  if (price < 0.001) return `$${price.toFixed(7)}`;
  if (price < 0.01) return `$${price.toFixed(6)}`;
  if (price < 0.1) return `$${price.toFixed(5)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(2)}`;
};

const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return 'RESOLVING...';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m ${secs}s`;
};

const ACCURACY_TIERS = {
  bullseye: { name: 'DIVINE SIGHT', sp: '+10 SP', color: '#ffd700', threshold: '1%', icon: Eye },
  hot: { name: 'TRUE VISION', sp: '+7 SP', color: '#ff8c00', threshold: '3%', icon: Flame },
  close: { name: 'FLICKERING', sp: '+4 SP', color: '#ff4500', threshold: '5%', icon: Target },
  warm: { name: 'HAZY', sp: '+2 SP', color: '#cc5500', threshold: '10%', icon: Clock },
  miss: { name: 'BLIND', sp: '+1 SP', color: '#4a3520', threshold: '>10%', icon: Lock },
};

const DemoBanner = () => (
  <div
    className="absolute top-0 left-0 right-0 z-30 pointer-events-none"
    data-testid="oracle-demo-banner"
  >
    <div
      className="mx-auto mt-3 px-3 sm:px-4 py-2 max-w-2xl flex items-center gap-3 pointer-events-auto"
      style={{
        background: 'linear-gradient(90deg, rgba(255,140,0,0.10) 0%, rgba(168,85,247,0.10) 100%)',
        border: '1px solid rgba(255,140,0,0.45)',
        borderRadius: 4,
        boxShadow: '0 0 18px rgba(255,140,0,0.25), inset 0 0 14px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <span
        className="inline-flex items-center justify-center flex-shrink-0"
        style={{
          width: 22, height: 22, borderRadius: '50%',
          background: 'rgba(255,140,0,0.18)',
          border: '1px solid rgba(255,140,0,0.55)',
          color: '#ffaa30',
          fontFamily: "'Cinzel', serif",
          fontSize: 11, fontWeight: 700,
        }}
      >
        ◆
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="font-mono uppercase font-bold"
          style={{
            fontSize: '0.62rem',
            letterSpacing: '0.28em',
            color: '#ffaa30',
            textShadow: '0 0 6px rgba(255,140,0,0.5)',
          }}
        >
          Demo Mode · Token Not Live
        </div>
        <div
          className="text-[0.7rem] sm:text-[0.74rem]"
          style={{
            color: 'rgba(230,220,200,0.78)',
            fontFamily: '"EB Garamond", Georgia, serif',
            fontStyle: 'italic',
            lineHeight: 1.35,
          }}
        >
          $GFG has not yet minted. Predictions resolve in <span style={{ color: '#ffaa30', fontStyle: 'normal' }}>60 seconds</span> against a simulated oracle.
        </div>
      </div>
    </div>
  </div>
);

// Verdict popup — appears front-and-center the moment a prediction resolves.
// Big tier name, win/loss visual, accuracy %, SP earned, and primary CTA.
const ResultModal = ({ result, predicted, actual, onClose, onPredictAgain }) => {
  if (!result) return null;
  const tier = ACCURACY_TIERS[result.tier] || ACCURACY_TIERS.miss;
  const Icon = tier.icon || Target;
  const won = result.tier !== 'miss';
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      data-testid="oracle-result-modal"
      style={{
        background: 'radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.95) 100%)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
        style={{
          background: 'linear-gradient(180deg, rgba(20,12,4,0.96) 0%, rgba(8,4,2,0.98) 100%)',
          border: `1px solid ${tier.color}`,
          borderRadius: 6,
          boxShadow: `0 0 56px ${tier.color}66, 0 0 120px ${tier.color}33, inset 0 0 50px rgba(0,0,0,0.55)`,
          padding: '36px 28px 26px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner brackets */}
        {['top-2 left-2 border-t border-l','top-2 right-2 border-t border-r','bottom-2 left-2 border-b border-l','bottom-2 right-2 border-b border-r'].map((cls, i) => (
          <span key={i} className={`absolute w-4 h-4 ${cls}`} style={{ borderColor: tier.color }} />
        ))}

        {/* Header — Verdict / Won / Lost */}
        <div className="text-center mb-5">
          <motion.div
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 260, damping: 18 }}
            className="inline-flex items-center justify-center mx-auto mb-3"
            style={{
              width: 76, height: 76, borderRadius: '50%',
              background: `radial-gradient(circle, ${tier.color}30 0%, transparent 70%)`,
              border: `2px solid ${tier.color}`,
              boxShadow: `0 0 28px ${tier.color}80`,
            }}
          >
            <Icon className="w-9 h-9" style={{ color: tier.color, filter: `drop-shadow(0 0 10px ${tier.color})` }} />
          </motion.div>
          <div
            className="font-mono uppercase font-bold mb-1"
            style={{
              fontSize: '0.72rem',
              letterSpacing: '0.36em',
              color: won ? '#ffd86b' : '#9ca3af',
              textShadow: won ? '0 0 10px rgba(255,215,0,0.55)' : 'none',
            }}
          >
            {won ? '◆ The Vision Held ◆' : '◇ The Flame Wavered ◇'}
          </div>
          <h2
            style={{
              fontFamily: "'Cinzel Decorative','Cinzel',serif",
              fontSize: 'clamp(1.7rem, 4vw, 2.4rem)',
              fontWeight: 900,
              letterSpacing: '0.08em',
              color: tier.color,
              textShadow: `0 0 16px ${tier.color}90`,
              lineHeight: 1.1,
            }}
          >
            {tier.name}
          </h2>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="text-center px-3 py-2.5" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,200,100,0.18)' }}>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: '#ffaa30' }}>Your Vision</div>
            <div className="font-bold mt-1" style={{ fontFamily: "'Courier New', monospace", color: '#fff5d6', fontSize: '0.95rem' }}>
              {formatPrice(predicted)}
            </div>
          </div>
          <div className="text-center px-3 py-2.5" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,200,100,0.18)' }}>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: '#ffaa30' }}>The Truth</div>
            <div className="font-bold mt-1" style={{ fontFamily: "'Courier New', monospace", color: '#fff5d6', fontSize: '0.95rem' }}>
              {formatPrice(actual)}
            </div>
          </div>
        </div>

        {/* Accuracy bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.22em] mb-1.5">
            <span style={{ color: '#ffaa30' }}>Accuracy</span>
            <span style={{ color: tier.color, fontWeight: 700 }}>{(result.accuracy ?? 0).toFixed(2)}% off</span>
          </div>
          <div className="h-2 relative overflow-hidden" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,200,100,0.18)' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(2, Math.min(100, 100 - (result.accuracy ?? 50)))}%` }}
              transition={{ duration: 1.1, ease: 'easeOut', delay: 0.4 }}
              className="absolute left-0 top-0 bottom-0"
              style={{
                background: `linear-gradient(90deg, ${tier.color}, ${tier.color}aa)`,
                boxShadow: `0 0 12px ${tier.color}80`,
              }}
            />
          </div>
        </div>

        {/* Reward — big SP gain */}
        <div
          className="text-center px-4 py-4 mb-5"
          style={{
            background: `linear-gradient(135deg, ${tier.color}22 0%, rgba(0,0,0,0.4) 100%)`,
            border: `1px solid ${tier.color}80`,
            boxShadow: `0 0 18px ${tier.color}40, inset 0 0 18px rgba(0,0,0,0.4)`,
          }}
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] mb-1" style={{ color: '#ffaa30' }}>Tribute Earned</div>
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.55, type: 'spring', stiffness: 220 }}
            style={{
              fontFamily: "'Impact','Arial Black',sans-serif",
              fontSize: 'clamp(2.2rem, 6vw, 3rem)',
              fontWeight: 900,
              color: tier.color,
              textShadow: `0 0 18px ${tier.color}, 0 0 38px ${tier.color}80`,
              lineHeight: 1,
            }}
          >
            +{result.points_earned} SP
          </motion.div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onPredictAgain}
            data-testid="result-predict-again-button"
            className="flex-1 py-3 font-bold uppercase tracking-[0.2em] transition-all hover:brightness-125"
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: '0.78rem',
              color: '#fff',
              background: `linear-gradient(135deg, ${tier.color}40 0%, ${tier.color}18 100%)`,
              border: `1px solid ${tier.color}`,
              boxShadow: `0 0 18px ${tier.color}40, inset 0 0 14px rgba(0,0,0,0.4)`,
              textShadow: `0 0 8px ${tier.color}, 0 1px 2px rgba(0,0,0,0.9)`,
            }}
          >
            Predict Again
          </button>
          <button
            onClick={onClose}
            data-testid="result-close-button"
            className="px-5 py-3 font-bold uppercase tracking-[0.2em] transition-all hover:brightness-125"
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: '0.72rem',
              color: 'rgba(255,200,100,0.85)',
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,200,100,0.32)',
            }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const PricePrediction = ({ onWin, onBack, stakerMultiplier = 1, walletAddress }) => {
  const [view, setView] = useState('menu');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [predictedPrice, setPredictedPrice] = useState(null);
  const [activePrediction, setActivePrediction] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [oraclePoints, setOraclePoints] = useState(0);
  const [predictionHistory, setPredictionHistory] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(true);  // assume demo until backend says otherwise

  const mockPriceRef = useRef(0.00000175 + (Math.random() - 0.5) * 0.0000002);

  const fetchData = useCallback(async () => {
    try {
      const priceRes = await fetch(`${API_URL}/api/predictions/current`);
      const priceData = await priceRes.json();
      if (priceData.current_price) {
        setCurrentPrice(priceData.current_price);
        if (predictedPrice === null) setPredictedPrice(priceData.current_price);
      }
      if (typeof priceData.demo_mode === 'boolean') setDemoMode(priceData.demo_mode);
      if (walletAddress) {
        const oracleRes = await fetch(`${API_URL}/api/price-oracle/${walletAddress}`);
        if (oracleRes.ok) {
          const oracleData = await oracleRes.json();
          setOraclePoints(oracleData.points || 0);
          setActivePrediction(oracleData.active_prediction || null);
          setPredictionHistory(oracleData.history || []);
          if (oracleData.active_prediction) {
            const endTime = new Date(oracleData.active_prediction.resolve_time).getTime();
            setTimeRemaining(Math.max(0, Math.floor((endTime - Date.now()) / 1000)));
          }
        }
      }
    } catch {
      if (currentPrice === null) {
        setCurrentPrice(mockPriceRef.current);
        if (predictedPrice === null) setPredictedPrice(mockPriceRef.current);
      }
    } finally { setLoading(false); }
  }, [walletAddress, predictedPrice, currentPrice]);

  // While in demo, refresh the live price every 5s so the dial drifts visibly.
  // Live mode keeps the existing 60s polling cadence.
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, demoMode ? 5000 : 60000);
    return () => clearInterval(interval);
  }, [fetchData, demoMode]);

  // ── Tick the countdown each second ─────────────────────────
  useEffect(() => {
    if (!activePrediction || timeRemaining <= 0) return;
    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [activePrediction, timeRemaining]);

  // ── Trigger resolution exactly once when timer hits 0 ──────
  // React 18 strict mode runs setState updaters twice in dev, which used to
  // double-invoke the resolve API and lock the UI in "resolving". We now use
  // a ref-guarded effect so resolve fires at most once per prediction.
  const resolvingRef = useRef(false);
  useEffect(() => {
    if (!activePrediction) {
      resolvingRef.current = false;
      return;
    }
    if (timeRemaining > 0) return;
    if (resolvingRef.current) return;
    resolvingRef.current = true;
    checkPredictionResult();
  }, [activePrediction, timeRemaining]);

  const checkPredictionResult = async () => {
    if (!walletAddress || !activePrediction) return;
    try {
      const res = await fetch(`${API_URL}/api/price-oracle/resolve/${walletAddress}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setLastResult(data.result);
        setOraclePoints(data.new_total_points);
      }
      // Whether resolve succeeded or returned 400 (already-resolved race), always
      // clear the active prediction and re-sync state so the UI never hangs.
      setActivePrediction(null);
      setTimeRemaining(0);
      fetchData();
    } catch (err) {
      console.error('Failed to resolve prediction:', err);
      // Even on network failure, drop the lock so the user isn't stuck.
      setActivePrediction(null);
      setTimeRemaining(0);
    } finally {
      resolvingRef.current = false;
    }
  };

  const submitPrediction = async () => {
    if (!walletAddress) return;
    try {
      const res = await fetch(`${API_URL}/api/price-oracle/predict`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, predicted_price: predictedPrice, current_price: currentPrice }),
      });
      if (res.ok) {
        const data = await res.json();
        setActivePrediction(data.prediction);
        // Trust the server-provided resolve_time (60s in demo, 24h live)
        const endTime = new Date(data.prediction.resolve_time).getTime();
        setTimeRemaining(Math.max(0, Math.floor((endTime - Date.now()) / 1000)));
      }
    } catch (err) { console.error('Failed to submit prediction:', err); }
  };

  const multiplier = currentPrice > 0 ? (predictedPrice || currentPrice) / currentPrice : 1;
  const percentChange = ((multiplier - 1) * 100).toFixed(1);

  // Verdict modal handlers — clearing `lastResult` unmounts the modal.
  // "Predict Again" additionally forces the view back to the prediction form.
  const handleResultClose = () => setLastResult(null);
  const handlePredictAgain = () => {
    setLastResult(null);
    setView('predict');
  };

  // Renders verdict popup centred on screen the moment a prediction resolves.
  const verdictModal = (
    <AnimatePresence>
      {lastResult && (
        <ResultModal
          result={lastResult}
          predicted={lastResult.predicted_price}
          actual={lastResult.actual_price}
          onClose={handleResultClose}
          onPredictAgain={handlePredictAgain}
        />
      )}
    </AnimatePresence>
  );

  if (loading) {
    return (
      <div className="relative h-screen w-full flex items-center justify-center"
        style={{ background: 'radial-gradient(ellipse at center, #0d0a05 0%, #0a0700 50%, #050300 100%)' }}>
        <motion.div
          className="w-16 h-16 rounded-full"
          style={{ border: '2px solid rgba(255,140,0,0.15)', borderTopColor: '#ff8c00' }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  // ============ CINEMATIC MENU ============
  if (view === 'menu') {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="oracle-menu"
        style={{ background: 'radial-gradient(ellipse at 50% 60%, #0d0a05 0%, #0a0700 30%, #050300 100%)' }}>
        <style>{CINEMATIC_KEYFRAMES}</style>
        <ScanlineOverlay />
        <NoiseOverlay />
        <GasParticles count={16} color="molten" />
        <ToxicDrips count={6} color="#ff8c00" />
        <SewerFrame color="rgba(255,140,0," />

        {/* DEMO MODE banner — token not yet live */}
        {demoMode && <DemoBanner />}

        {/* Orb background silhouette */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.08, scale: 1 }}
            transition={{ duration: 2.5 }}
            className="w-72 h-72 md:w-96 md:h-96 rounded-full relative"
            style={{ border: '2px solid rgba(255,140,0,0.12)', boxShadow: '0 0 80px rgba(255,140,0,0.04), inset 0 0 60px rgba(255,140,0,0.03)' }}
          >
            <div className="absolute inset-6 rounded-full border border-orange-400/8" />
            <div className="absolute inset-12 rounded-full border border-orange-400/5" />
            {/* Mystical rune ticks */}
            {[...Array(24)].map((_, i) => (
              <div key={i} className="absolute w-px" style={{
                height: i % 6 === 0 ? '12px' : '6px',
                left: '50%', top: '4px',
                transformOrigin: '50% calc(50% + 140px)',
                transform: `translateX(-50%) rotate(${i * 15}deg)`,
                background: `rgba(255,140,0,${i % 6 === 0 ? 0.2 : 0.06})`,
              }} />
            ))}
            {/* Center glow */}
            <motion.div className="absolute inset-0 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(255,140,0,0.1) 0%, transparent 50%)' }}
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-center relative"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-[0.15em] uppercase relative"
              style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}
              data-testid="cinematic-title"
            >
              <span style={{
                background: 'linear-gradient(180deg, #ffd700 0%, #ff8c00 30%, #ff4500 60%, #cc2200 85%, #8b1a00 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 15px rgba(255,69,0,0.5)) drop-shadow(0 2px 4px rgba(0,0,0,0.8))',
              }}>Price Oracle</span>
            </h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="text-sm md:text-base tracking-[0.3em] uppercase mt-2"
              style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.6)' }}
            >
              Predict what burns next
            </motion.p>
          </motion.div>

          {/* Lore Description */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="max-w-md text-center mt-6 px-6 py-4"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,140,0,0.08)' }}
          >
            <p className="text-[11px] leading-relaxed" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,160,64,0.55)' }}>
              The oracle chamber reveals fragments of what's to come. Lock your {demoMode ? '60-second' : '24-hour'} prophecy on $GFG's price. The closer your vision, the greater the reward.
            </p>
          </motion.div>

          {/* SP Reward Tiers Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="flex gap-3 mt-6"
          >
            {[
              { name: 'Divine Sight', sp: '+10 SP', color: '#ffd700', threshold: '1%' },
              { name: 'True Vision', sp: '+7 SP', color: '#ff8c00', threshold: '3%' },
              { name: 'Flickering', sp: '+4 SP', color: '#ff4500', threshold: '5%' },
              { name: 'Hazy', sp: '+2 SP', color: '#cc5500', threshold: '10%' },
            ].map((tier, i) => (
              <div key={i} className="text-center px-3 py-2" style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${tier.color}18` }}>
                <div className="text-[9px] font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: `${tier.color}90` }}>{tier.name.toUpperCase()}</div>
                <div className="text-base font-black mt-0.5" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", color: tier.color }}>{tier.sp}</div>
                <div className="text-[8px]" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Courier New', monospace" }}>within {tier.threshold}</div>
              </div>
            ))}
          </motion.div>

          {/* Current Price Preview */}
          {currentPrice > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="mt-5 flex items-center gap-2 px-4 py-2"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,140,0,0.1)' }}
            >
              <span className="text-[9px] uppercase tracking-[0.2em]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,160,64,0.5)' }}>Current $GFG:</span>
              <span className="text-sm font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.5)' }}>{formatPrice(currentPrice)}</span>
            </motion.div>
          )}

          {/* Enter Button */}
          <motion.button
            onClick={() => setView('predict')}
            data-testid="enter-oracle-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            className="mt-6 px-10 py-4 font-bold text-base tracking-[0.15em] uppercase relative overflow-hidden"
            style={{
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,140,0,0.4)',
              fontFamily: "'Cinzel', serif",
              animation: 'pulseGlow 3s ease-in-out infinite',
            }}
            whileHover={{ scale: 1.02, borderColor: 'rgba(255,140,0,0.7)', boxShadow: '0 0 30px rgba(255,69,0,0.15)' }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="relative z-10 flex items-center gap-3" style={{
              background: 'linear-gradient(90deg, #ffd700, #ff8c00)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              <Eye className="w-5 h-5" style={{ color: '#ff8c00' }} />
              Consult the Oracle
            </span>
            <motion.div className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.08), transparent)' }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} />
          </motion.button>

          {/* Status Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2"
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#ff8c00', boxShadow: '0 0 6px #ff8c00', animation: 'warningPulse 2s ease-in-out infinite' }} />
            <span className="text-[9px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,160,64,0.55)' }}>
              Oracle Chamber: Active // {demoMode ? '60s Demo Window' : '24H Window'} // +10 SP Max
            </span>
          </motion.div>
        </div>
        {verdictModal}
      </div>
    );
  }

  return (
    <div className="relative h-screen w-full overflow-hidden" data-testid="price-oracle-page"
      style={{ background: 'radial-gradient(ellipse at 50% 30%, #0d0a05 0%, #0a0700 40%, #050300 100%)' }}>
      <style>{CINEMATIC_KEYFRAMES}</style>
      <ScanlineOverlay />
      <NoiseOverlay />
      <GasParticles count={10} color="molten" />
      <ToxicDrips count={3} color="#ff8c00" />
      <SewerFrame color="rgba(255,140,0," />

      {/* DEMO MODE banner — token not yet live */}
      {demoMode && <DemoBanner />}

      {/* Ambient orb glow in center */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0">
        <motion.div
          className="w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,140,0,0.06) 0%, rgba(255,69,0,0.02) 40%, transparent 70%)', filter: 'blur(60px)' }}
          animate={{ scale: [0.9, 1.1, 0.9], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Full-screen grid layout */}
      <div className="relative z-20 h-full flex flex-col px-6 lg:px-12">

        {/* ═══ HEADER + NAV ═══ */}
        <div className="flex-shrink-0 pt-20 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-bold uppercase tracking-wider inline-block"
            style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}
            data-testid="oracle-title"
          >
            <span style={{
              background: 'linear-gradient(180deg, #ffd700 0%, #ff8c00 30%, #ff4500 60%, #cc2200 85%, #8b1a00 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 15px rgba(255,69,0,0.5))',
            }}>Price Oracle</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="text-[10px] tracking-[0.4em] uppercase mt-0.5 mb-1.5"
            style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.6)' }}>
            Gaze into the flames. Predict what burns next.
          </motion.p>
          <div className="flex items-center justify-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1" style={{ border: '1px solid rgba(255,140,0,0.15)', background: 'rgba(0,0,0,0.4)' }}>
              <Flame className="w-3.5 h-3.5" style={{ color: '#ff8c00' }} />
              <span className="text-sm font-bold tracking-wide" data-testid="oracle-points"
                style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", background: 'linear-gradient(90deg, #ffd700, #ff8c00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {oraclePoints.toLocaleString()}
              </span>
              <span className="text-[9px] uppercase tracking-wider" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,160,64,0.55)' }}>pts</span>
            </div>
            {[{ id: 'predict', label: 'The Ritual', icon: Eye }, { id: 'history', label: 'Scrolls', icon: Scroll }].map((tab) => (
              <motion.button key={tab.id} onClick={() => setView(tab.id)} data-testid={`tab-${tab.id}`}
                className="flex items-center gap-2 px-3 py-1 transition-all"
                style={{
                  background: view === tab.id ? 'rgba(255,140,0,0.08)' : 'rgba(0,0,0,0.3)',
                  border: `1px solid ${view === tab.id ? 'rgba(255,140,0,0.35)' : 'rgba(255,255,255,0.04)'}`,
                }}
                whileHover={{ borderColor: 'rgba(255,140,0,0.3)' }}>
                <tab.icon className="w-3.5 h-3.5" style={{ color: view === tab.id ? '#ff8c00' : 'rgba(255,255,255,0.3)' }} />
                <span className="text-[11px] uppercase tracking-[0.12em] font-bold"
                  style={{ fontFamily: "'Cinzel', serif", color: view === tab.id ? '#ff8c00' : 'rgba(255,255,255,0.35)' }}>
                  {tab.label}
                </span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* ═══ MAIN CONTENT ═══ */}
        <div className="flex-1 flex items-center justify-center min-h-0 overflow-hidden">
          <AnimatePresence mode="wait">

            {/* ═══ PREDICT VIEW ═══ */}
            {view === 'predict' && (
              <motion.div key="predict" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="w-full max-w-4xl">

                {activePrediction ? (
                  /* ── Active Prediction: Waiting for Resolution ── */
                  <div className="grid grid-cols-3 gap-4 items-center">
                    {/* Left: Your Prediction */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
                      className="p-6 text-center"
                      style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.15)' }}
                    >
                      <p className="text-[9px] uppercase tracking-[0.3em] mb-2" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.6)' }}>Your Vision</p>
                      <p className="text-2xl font-bold" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", background: 'linear-gradient(180deg, #ffd700, #ff8c00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 0 8px rgba(255,140,0,0.4))' }}>
                        {formatPrice(activePrediction.predicted_price)}
                      </p>
                    </motion.div>

                    {/* Center: Timer Orb */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                      className="text-center flex flex-col items-center"
                    >
                      <div className="relative w-40 h-40 flex items-center justify-center">
                        {/* Rotating ring */}
                        <motion.div className="absolute inset-0 rounded-full"
                          style={{ border: '2px solid rgba(255,140,0,0.15)', borderTopColor: '#ff8c00', borderRightColor: 'rgba(255,69,0,0.3)' }}
                          animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: 'linear' }} />
                        <motion.div className="absolute inset-2 rounded-full"
                          style={{ border: '1px solid rgba(255,140,0,0.08)', borderBottomColor: 'rgba(255,215,0,0.2)' }}
                          animate={{ rotate: -360 }} transition={{ duration: 12, repeat: Infinity, ease: 'linear' }} />
                        {/* Inner glow */}
                        <div className="absolute inset-4 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,140,0,0.08) 0%, transparent 70%)' }} />
                        {/* Timer */}
                        <div className="relative z-10">
                          <p className="text-3xl font-black tracking-wider" style={{
                            fontFamily: "'Impact', 'Arial Black', sans-serif",
                            background: timeRemaining < 3600 ? 'linear-gradient(180deg, #ffd700, #ff4500)' : 'linear-gradient(180deg, #ffd700, #ff8c00)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            filter: 'drop-shadow(0 0 10px rgba(255,140,0,0.4))',
                          }}>
                            {formatTimeRemaining(timeRemaining)}
                          </p>
                          <p className="text-[8px] uppercase tracking-[0.3em] mt-1" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,160,64,0.5)' }}>Until Resolution</p>
                        </div>
                      </div>
                      <motion.p className="text-[10px] uppercase tracking-[0.2em] mt-2"
                        style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.5)' }}
                        animate={{ opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 3, repeat: Infinity }}>
                        The flames are reading...
                      </motion.p>
                    </motion.div>

                    {/* Right: Current Price */}
                    <motion.div
                      initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
                      className="p-6 text-center"
                      style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.04)' }}
                    >
                      <p className="text-[9px] uppercase tracking-[0.3em] mb-2" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.4)' }}>Current Reality</p>
                      <p className="text-2xl font-bold" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", color: 'rgba(255,255,255,0.5)' }}>
                        {formatPrice(currentPrice)}
                      </p>
                    </motion.div>
                  </div>

                ) : (
                  /* ── New Prediction Form ── */
                  <div className="grid grid-cols-12 gap-5 items-start">

                    {/* Left: Current Price + Slider */}
                    <div className="col-span-5">
                      {/* Current Price Orb */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="p-5 mb-4 text-center relative overflow-hidden"
                        style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.12)' }}>
                        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(255,140,0,0.04) 0%, transparent 60%)' }} />
                        <p className="text-[9px] uppercase tracking-[0.3em] mb-2 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.6)' }}>The Present Flame</p>
                        <p className="text-3xl font-black relative z-10" data-testid="current-price"
                          style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", color: 'rgba(255,255,255,0.6)', textShadow: '0 0 15px rgba(255,255,255,0.1)' }}>
                          {formatPrice(currentPrice)}
                        </p>
                        <p className="text-[8px] uppercase tracking-[0.2em] mt-1 relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.35)' }}>$GFG / USD</p>
                      </motion.div>

                      {/* Prediction Slider */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                        className="p-5" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,140,0,0.08)' }}>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.55)' }}>Your Prophecy</span>
                          <span className="text-sm font-bold" style={{
                            fontFamily: "'Courier New', monospace",
                            color: multiplier > 1 ? '#ffd700' : multiplier < 1 ? '#ff4500' : 'rgba(255,255,255,0.3)',
                          }}>
                            {multiplier > 1 ? '+' : ''}{percentChange}%
                            {multiplier > 1 ? <ArrowUp className="w-3 h-3 inline ml-1" /> : multiplier < 1 ? <ArrowDown className="w-3 h-3 inline ml-1" /> : null}
                          </span>
                        </div>

                        <input type="range" min={0.5} max={1.5} step={0.01} value={multiplier}
                          onChange={(e) => setPredictedPrice(currentPrice * parseFloat(e.target.value))}
                          data-testid="prediction-slider"
                          className="w-full h-1.5 appearance-none cursor-pointer rounded-full"
                          style={{ background: `linear-gradient(90deg, #ff4500, #ff8c00 ${((multiplier - 0.5) / 1) * 100}%, rgba(255,255,255,0.06) ${((multiplier - 0.5) / 1) * 100}%)`, accentColor: '#ff8c00' }} />

                        <div className="flex justify-between text-[9px] mt-1" style={{ color: 'rgba(255,160,64,0.45)', fontFamily: "'Courier New', monospace" }}>
                          <span>-50%</span><span>0%</span><span>+50%</span>
                        </div>

                        {/* Predicted Price */}
                        <div className="text-center mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,140,0,0.08)' }}>
                          <p className="text-[9px] uppercase tracking-[0.3em] mb-1" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.55)' }}>Your Vision</p>
                          <p className="text-3xl font-black" data-testid="predicted-price"
                            style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", background: 'linear-gradient(180deg, #ffd700, #ff8c00, #ff4500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 0 12px rgba(255,140,0,0.4))' }}>
                            {formatPrice(predictedPrice)}
                          </p>
                        </div>
                      </motion.div>
                    </div>

                    {/* Right: Tiers + Submit */}
                    <div className="col-span-7">
                      {/* Accuracy Tiers Grid */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                        className="p-5 mb-4" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,140,0,0.08)' }}>
                        <p className="text-[9px] uppercase tracking-[0.3em] mb-4" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.55)' }}>Prophecy Accuracy Ranks</p>

                        <div className="space-y-2">
                          {Object.entries(ACCURACY_TIERS).map(([key, tier]) => {
                            const TierIcon = tier.icon;
                            return (
                              <div key={key} className="flex items-center justify-between px-4 py-2.5 group"
                                style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${tier.color}12` }}>
                                <div className="flex items-center gap-3">
                                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${tier.color}10`, border: `1px solid ${tier.color}25` }}>
                                    <TierIcon className="w-3.5 h-3.5" style={{ color: tier.color }} />
                                  </div>
                                  <div>
                                    <span className="text-sm font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: tier.color }}>{tier.name}</span>
                                    <span className="text-[10px] ml-2" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.35)' }}>within {tier.threshold}</span>
                                  </div>
                                </div>
                                <div className="px-2.5 py-1" style={{
                                  background: `linear-gradient(135deg, ${tier.color}20, ${tier.color}08)`,
                                  border: `1px solid ${tier.color}25`,
                                  transform: 'skewX(-8deg)',
                                }}>
                                  <span className="text-xs font-bold tracking-wide" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", color: tier.color }}>{tier.sp}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>

                      {/* Submit / Connect */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        {walletAddress ? (
                          <motion.button onClick={submitPrediction} data-testid="submit-prediction-btn"
                            className="w-full py-4 font-bold text-lg relative overflow-hidden tracking-wider"
                            style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,140,0,0.35)', fontFamily: "'Cinzel', serif" }}
                            whileHover={{ scale: 1.01, borderColor: 'rgba(255,140,0,0.6)', boxShadow: '0 0 30px rgba(255,69,0,0.15)' }}
                            whileTap={{ scale: 0.99 }}>
                            {/* Shimmer */}
                            <motion.div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.06), transparent)' }}
                              animate={{ x: ['-100%', '100%'] }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
                            <span className="relative z-10 flex items-center justify-center gap-3" style={{
                              background: 'linear-gradient(90deg, #ffd700, #ff8c00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            }}>
                              <Lock className="w-5 h-5" style={{ color: '#ff8c00' }} />
                              Seal the Prophecy ({demoMode ? '60s' : '24H'})
                            </span>
                          </motion.button>
                        ) : (
                          <div className="py-4 text-center" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.04)' }}>
                            <Lock className="w-5 h-5 mx-auto mb-1.5" style={{ color: 'rgba(255,140,0,0.2)' }} />
                            <p className="text-sm" style={{ color: 'rgba(255,160,64,0.5)', fontFamily: "'Cinzel', serif" }}>Connect wallet to consult the oracle</p>
                          </div>
                        )}
                      </motion.div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* ═══ HISTORY VIEW ═══ */}
            {view === 'history' && (
              <motion.div key="history" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="w-full max-w-3xl">
                <div className="p-6 max-h-[60vh] overflow-y-auto" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,140,0,0.1)' }}>
                  <div className="flex items-center gap-2 mb-5">
                    <History className="w-4 h-4" style={{ color: 'rgba(255,140,0,0.4)' }} />
                    <span className="text-sm font-bold uppercase tracking-[0.15em]" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.45)' }}>Ancient Scrolls</span>
                    <div className="flex-1 h-px ml-2" style={{ background: 'linear-gradient(90deg, rgba(255,140,0,0.15), transparent)' }} />
                  </div>

                  {predictionHistory.length === 0 ? (
                    <div className="text-center py-16">
                      <Scroll className="w-12 h-12 mx-auto mb-3" style={{ color: 'rgba(255,140,0,0.1)' }} />
                      <p className="text-sm" style={{ color: 'rgba(255,160,64,0.5)', fontFamily: "'Cinzel', serif" }}>The scrolls are empty</p>
                      <p className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Courier New', monospace" }}>Make your first prophecy to inscribe the record</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {predictionHistory.map((pred, i) => {
                        const tier = ACCURACY_TIERS[pred.tier];
                        return (
                          <motion.div key={i}
                            initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                            className="flex items-center justify-between px-4 py-3"
                            style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${tier?.color || 'rgba(255,255,255,0.03)'}12` }}>
                            <div className="flex items-center gap-3">
                              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${tier?.color || '#4a3520'}10` }}>
                                <Target className="w-3.5 h-3.5" style={{ color: tier?.color || '#4a3520', opacity: 0.6 }} />
                              </div>
                              <div>
                                <div className="text-sm font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.4)' }}>{formatPrice(pred.predicted_price)}</div>
                                <div className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.35)' }}>Actual: {formatPrice(pred.actual_price)}</div>
                              </div>
                            </div>
                            <div className="text-right flex items-center gap-4">
                              <span className="text-xs font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: tier?.color || '#4a3520' }}>{tier?.name}</span>
                              <div className="px-2 py-0.5" style={{ background: `${tier?.color || '#4a3520'}12`, border: `1px solid ${tier?.color || '#4a3520'}20`, transform: 'skewX(-8deg)' }}>
                                <span className="font-bold text-sm" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", color: tier?.color || '#4a3520' }}>+{pred.points_earned}</span>
                              </div>
                              <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Courier New', monospace" }}>{new Date(pred.resolved_at).toLocaleDateString()}</span>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ═══ BOTTOM STATUS ═══ */}
        <div className="flex-shrink-0 py-2 text-center">
          <motion.div className="flex items-center justify-center gap-2"
            animate={{ opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 4, repeat: Infinity }}>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#ff8c00', boxShadow: '0 0 6px #ff8c00' }} />
            <span className="text-[9px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,160,64,0.55)' }}>
              Oracle Chamber Active // {demoMode ? '60s Demo Window' : '24H Prediction Window'} // +10 SP Max
            </span>
          </motion.div>
        </div>
      </div>
      {verdictModal}
    </div>
  );
};

export default PricePrediction;
